/**
 * Centralised, validated access to Vite env vars.
 *
 * Single source of truth for `import.meta.env.VITE_*` lookups so that
 * defaults, types, and naming are consistent across the app. Missing or
 * empty values silently fall back to a localhost default (current, actual
 * behavior -- nothing throws at import time). `VITE_PAYMENT_MODE` falls
 * back to `'live'` for any value other than the literal `'mock'`.
 */

import { z } from 'zod';

type PaymentMode = 'live' | 'mock';

const rawEnv = import.meta.env as Record<string, string | undefined>;

/** Non-empty string, else `fallback` -- mirrors the previous readString() behavior exactly. */
const urlVar = (fallback: string) => z.string().min(1).catch(fallback);

const paymentModeSchema = z
  .string()
  .optional()
  .transform((v): PaymentMode => (v === 'mock' ? 'mock' : 'live'));

export const env = {
  identityBaseUrl: urlVar('http://localhost:3002').parse(rawEnv.VITE_IDENTITY_SERVICE_URL),
  kioskBaseUrl: urlVar('http://localhost:3009').parse(rawEnv.VITE_KIOSK_API_URL),
  fnbBaseUrl: urlVar('http://localhost:3014').parse(rawEnv.VITE_FNB_SERVICE_URL),
  ticketingBaseUrl: urlVar('http://localhost:3001').parse(rawEnv.VITE_TICKETING_API_URL),
  wristbandBaseUrl: urlVar('http://localhost:3012').parse(rawEnv.VITE_WRISTBAND_SERVICE_URL),
  capacityBaseUrl: urlVar('http://localhost:3007').parse(rawEnv.VITE_CAPACITY_SERVICE_URL),
  analyticsBaseUrl: urlVar('http://localhost:3006').parse(rawEnv.VITE_ANALYTICS_SERVICE_URL),
  paymentMode: paymentModeSchema.parse(rawEnv.VITE_PAYMENT_MODE),
  midtransClientKey: z.string().optional().parse(rawEnv.VITE_MIDTRANS_CLIENT_KEY),
  midtransSnapUrl: z
    .string()
    .url()
    .optional()
    .parse(rawEnv.VITE_MIDTRANS_SNAP_URL),
} as const;

export type Env = typeof env;

export const PAYMENT_MODE = env.paymentMode;
